import React, { useCallback, useEffect, useMemo, useState } from "react";

import { fetchAdminFinancialDashboard } from "../services/adminService";
import { getProjectLifecycleLabel, sortProjectsNewestFirst } from "../utils/projectViews";

const KPI_GROUPS = [
  {
    title: "P&L & profitability",
    keys: [
      { key: "total_company_revenue_bdt", label: "Total company revenue" },
      { key: "total_company_expenses_bdt", label: "Total company expenses" },
      { key: "total_net_profit_loss_bdt", label: "Net profit / loss" },
      { key: "total_approved_pnl_net_bdt", label: "Approved P&L (net)" },
      { key: "total_asset_management_fees_bdt", label: "Asset management fees" },
    ],
  },
  {
    title: "Capital & fundraising",
    keys: [
      { key: "total_fundraising_bdt", label: "Fundraising (primary)" },
      { key: "total_investor_contributions_bdt", label: "Investor contributions" },
      { key: "total_operational_expenses_bdt", label: "Op. expenses (period)" },
      { key: "total_redemption_paid_bdt", label: "Redemptions paid (period)" },
      { key: "total_remaining_project_fund_bdt", label: "Remaining project fund" },
      { key: "total_ultimate_pnl_bdt", label: "Ultimate P&L (ledger)" },
      { key: "total_deposits_bdt", label: "Deposits (approved)" },
      { key: "total_withdrawals_bdt", label: "Withdrawals (paid out)" },
    ],
  },
  {
    title: "Platform fees & treasury",
    keys: [
      { key: "total_transfer_fee_earnings_bdt", label: "Transfer fee earnings (cash)" },
      { key: "total_transfer_fee_share_bdt", label: "Transfer fees (SSA share notional)" },
      { key: "total_redemption_fee_ssa_notional_bdt", label: "Redemption fees (SSA notional)" },
      { key: "total_platform_fee_earnings_bdt", label: "Total platform fees" },
      { key: "total_treasury_balance_bdt", label: "Treasury balance (NAV)" },
      { key: "total_aum_bdt", label: "Assets under management" },
    ],
  },
  {
    title: "Scale",
    keys: [
      {
        key: "total_active_projects",
        label: "Pipeline projects",
        currency: false,
        detail: "Draft through matured (excludes redeemed, closed, cancelled)",
      },
      { key: "total_investors", label: "Active investors", currency: false },
      { key: "total_approved_pnl_count", label: "Approved P&L records", currency: false },
    ],
  },
];

const PROJECT_COLUMNS = [
  { key: "project_code", label: "Code" },
  { key: "project_name", label: "Project" },
  { key: "status", label: "Status" },
  { key: "asset_manager_name", label: "Asset manager" },
  { key: "pnl_revenue_bdt", label: "P&L revenue (period)", money: true },
  { key: "pnl_expenses_bdt", label: "P&L expenses (period)", money: true },
  { key: "net_pnl_bdt", label: "Net P&L (period)", money: true },
  { key: "fundraising_bdt", label: "Fundraising (period)", money: true },
  { key: "operational_expenses_bdt", label: "Op. expenses (period)", money: true },
  { key: "funds_raised_bdt", label: "Funds raised (ledger)", money: true },
  { key: "transfer_fee_income_bdt", label: "Fee income (ledger)", money: true },
  { key: "redemption_paid_bdt", label: "Redemptions paid (period)", money: true },
  { key: "redemption_paid_lifetime_bdt", label: "Redemptions paid (ledger)", money: true },
  { key: "remaining_project_fund_bdt", label: "Remaining fund", money: true, strong: true },
  { key: "ultimate_pnl_bdt", label: "Ultimate P&L", money: true, strong: true },
  { key: "transfer_fee_bdt", label: "Transfer fees (period)", money: true },
  { key: "redemption_fee_bdt", label: "Redemption fees (SSA)", money: true },
  { key: "asset_management_fees_bdt", label: "AM fees", money: true },
  { key: "nav_per_share_bdt", label: "NAV / sh", money: true },
  { key: "aum_bdt", label: "AUM", money: true },
];

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return v ?? "—";
  return `BDT ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function KpiCard({ label, value, currency = true, detail }) {
  const display = currency ? fmtMoney(value) : String(value ?? "—");
  const n = currency ? Number(value) : null;
  const tone = currency && Number.isFinite(n) && n < 0 ? "fin-report-kpi--neg" : "";
  return (
    <div className={`fin-report-kpi ${tone}`}>
      <div className="fin-report-kpi__label">{label}</div>
      <div className="fin-report-kpi__value">{display}</div>
      {detail ? <p className="fin-report-kpi__detail muted">{detail}</p> : null}
    </div>
  );
}

export default function AdminFinancialReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    preset: "THIS_MONTH",
    date_from: "",
    date_to: "",
    project_id: "",
    asset_manager_id: "",
    project_status: "",
    transaction_type: "ALL",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        preset: filters.preset,
        date_from: filters.preset === "CUSTOM" ? filters.date_from : undefined,
        date_to: filters.preset === "CUSTOM" ? filters.date_to : undefined,
        project_id: filters.project_id || undefined,
        asset_manager_id: filters.asset_manager_id || undefined,
        project_status: filters.project_status || undefined,
        transaction_type: filters.transaction_type || undefined,
      };
      const res = await fetchAdminFinancialDashboard(payload);
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.detail || e.message || "Failed to load financial report.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const filterOptions = data?.filter_options || {};
  const kpis = data?.company_kpis || {};
  const projectRows = data?.projects || [];
  const projectRowsSorted = useMemo(() => sortProjectsNewestFirst(projectRows), [projectRows]);
  const meta = data?.meta || {};

  const projectOptions = useMemo(
    () =>
      projectRows.map((p) => ({
        id: p.project_id ?? p.id,
        label: `${p.project_code || "—"} — ${p.project_name || p.name || "—"}`,
      })),
    [projectRows]
  );

  return (
    <div className="page fin-report-page">
      <header className="fin-report-header">
        <div>
          <h1 className="fin-report-title">Financial performance report</h1>
          <p className="fin-report-subtitle">
            Consolidated company view and project-level detail ·{" "}
            <strong>{meta.period_label || "—"}</strong>
            {meta.generated_at ? (
              <span className="muted"> · Updated {String(meta.generated_at).slice(0, 19).replace("T", " ")} UTC</span>
            ) : null}
          </p>
          <p className="muted fin-report-note">
            Period columns respect the date filter. Draft, review, and paused projects always appear (often zero).
            Cancelled projects are hidden unless you filter by Cancelled status.
            Remaining fund and ultimate P&amp;L are ledger positions (frozen when a project is closed). Operational
            expenses use the asset-manager expense ledger; P&amp;L revenue/expenses are from approved P&amp;L
            submissions only.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => load()} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <section className="card fin-report-filters">
        <h2 className="fin-report-section-title">Filters</h2>
        <div className="fin-report-filters__grid">
          <label>
            <span className="fin-report-filter-label">Period</span>
            <select
              className="compact-field"
              value={filters.preset}
              onChange={(e) => setFilters((f) => ({ ...f, preset: e.target.value }))}
            >
              {(filterOptions.date_presets || []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {filters.preset === "CUSTOM" ? (
            <>
              <label>
                <span className="fin-report-filter-label">From</span>
                <input
                  type="date"
                  className="compact-field"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                />
              </label>
              <label>
                <span className="fin-report-filter-label">To</span>
                <input
                  type="date"
                  className="compact-field"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                />
              </label>
            </>
          ) : null}
          <label>
            <span className="fin-report-filter-label">Project</span>
            <select
              className="compact-field"
              value={filters.project_id}
              onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value }))}
            >
              <option value="">All projects</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="fin-report-filter-label">Asset manager</span>
            <select
              className="compact-field"
              value={filters.asset_manager_id}
              onChange={(e) => setFilters((f) => ({ ...f, asset_manager_id: e.target.value }))}
            >
              <option value="">All managers</option>
              {(filterOptions.asset_managers || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="fin-report-filter-label">Project status</span>
            <select
              className="compact-field"
              value={filters.project_status}
              onChange={(e) => setFilters((f) => ({ ...f, project_status: e.target.value }))}
            >
              <option value="">Any status</option>
              {(filterOptions.project_statuses || []).map((st) => (
                <option key={st} value={st}>
                  {getProjectLifecycleLabel(st)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="fin-report-filter-label">Transaction type</span>
            <select
              className="compact-field"
              value={filters.transaction_type}
              onChange={(e) => setFilters((f) => ({ ...f, transaction_type: e.target.value }))}
            >
              {(filterOptions.transaction_types || [{ value: "ALL", label: "All types" }]).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {error ? <p className="msg msg-error">{error}</p> : null}

      {loading && !data ? (
        <div className="fin-report-kpi-grid fin-report-kpi-grid--loading">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="fin-report-kpi fin-report-kpi--skeleton" />
          ))}
        </div>
      ) : null}

      {data ? (
        <>
          {KPI_GROUPS.map((group) => (
            <section key={group.title} className="fin-report-kpi-section">
              <h2 className="fin-report-section-title">{group.title}</h2>
              <div className="fin-report-kpi-grid">
                {group.keys.map(({ key, label, currency, detail }) => (
                  <KpiCard
                    key={key}
                    label={label}
                    value={kpis[key]}
                    currency={currency !== false}
                    detail={detail}
                  />
                ))}
              </div>
            </section>
          ))}

          <section className="card fin-report-projects">
            <div className="fin-report-projects__head">
              <h2 className="fin-report-section-title">Project-wise financial detail</h2>
              <span className="muted">{projectRows.length} project(s)</span>
            </div>
            <div className="table-wrap fin-report-table-wrap">
              <table className="ops-table fin-report-table">
                <thead>
                  <tr>
                    {PROJECT_COLUMNS.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projectRowsSorted.map((row) => (
                    <tr
                      key={row.project_id}
                      className={row.is_closed ? "fin-report-table__row--closed" : undefined}
                    >
                      {PROJECT_COLUMNS.map((col) => {
                        let v = row[col.key];
                        if (col.key === "status") {
                          return (
                            <td key={col.key}>
                              <span className={`badge badge-${v}`}>{getProjectLifecycleLabel(v)}</span>
                              {row.expenses_locked ? (
                                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                  Expenses locked
                                </div>
                              ) : null}
                            </td>
                          );
                        }
                        if (col.money) v = fmtMoney(v);
                        const cell = v == null || v === "" ? "—" : v;
                        return col.strong ? (
                          <td key={col.key}>
                            <strong>{cell}</strong>
                          </td>
                        ) : (
                          <td key={col.key}>{cell}</td>
                        );
                      })}
                    </tr>
                  ))}
                  {!projectRows.length && !loading ? (
                    <tr>
                      <td colSpan={PROJECT_COLUMNS.length} className="history-empty">
                        No projects match the current filters.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
